import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { SEO } from "@/lib/seo";
import { MarketingHeader } from "@/components/marketing/MarketingHeader";
import { MarketingFooter } from "@/components/marketing/MarketingSections2";
import api from "@/lib/api";
import { Trash2, ShieldCheck, Clock, Mail, Loader2, CheckCircle2 } from "lucide-react";

const PRIMARY = "#FF5F15";

export default function DeleteAccount() {
  const [form, setForm] = useState({ name: "", email: "", organisation: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/public/account-deletion-request", {
        name: form.name,
        email: form.email,
        organisation: form.organisation,
        reason: form.reason,
      }, { __flOnlineOnly: true });
      setDone(true);
      toast.success(data?.message || "Your request has been received.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send your request. Please email support@fieldlink.co.nz.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <SEO
        title="Delete your Fieldlink account | Fieldlink NZ"
        description="How to request deletion of your Fieldlink NZ account and associated data, what is deleted or kept, and retention periods."
        path="/delete-account"
      />
      <div className="min-h-screen bg-[#F4F4F1] text-[#161A20]" id="top" data-testid="delete-account-page">
        <div><MarketingHeader /></div>

        <header className="border-b border-[#161A20]/10 bg-white">
          <div className="max-w-[900px] mx-auto px-6 md:px-10 py-12 md:py-16">
            <div className="text-[11px] font-mono font-bold uppercase tracking-[0.32em] text-[#FF5F15] mb-4">Fieldlink NZ</div>
            <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl text-[#161A20]" data-testid="delete-account-title">
              Delete your account
            </h1>
            <p className="mt-4 text-base md:text-lg text-[#161A20]/60 max-w-2xl">
              Request deletion of your Fieldlink NZ account and the personal data associated with it.
              This page applies to the Fieldlink NZ app published by Fieldlink.
            </p>
          </div>
        </header>

        <main className="max-w-[900px] mx-auto px-6 md:px-10 py-10 lg:py-14 space-y-10">
          {/* Steps */}
          <section>
            <h2 className="font-display font-black uppercase tracking-tight text-2xl mb-4 flex items-center gap-2">
              <Trash2 className="w-5 h-5" style={{ color: PRIMARY }} /> How to request deletion
            </h2>
            <ol className="space-y-3 text-[15px] leading-relaxed text-[#161A20]/80 list-decimal pl-5" data-testid="delete-account-steps">
              <li><b>In the app:</b> open <b>Profile</b>, scroll to <b>Request account deletion</b>, and confirm. Your request is sent to our team automatically.</li>
              <li><b>Or on this page:</b> fill in the form below and submit it.</li>
              <li><b>Or by email:</b> send a request to <a href="mailto:support@fieldlink.co.nz?subject=Account%20deletion%20request" className="font-bold" style={{ color: PRIMARY }}>support@fieldlink.co.nz</a> with the subject “Account deletion request”.</li>
            </ol>
            <p className="mt-4 text-sm text-[#161A20]/60">
              We verify you own the account (to protect your employer’s records), then confirm by email and
              action the request within <b>30 days</b>.
            </p>
          </section>

          {/* What is deleted / kept */}
          <section className="grid md:grid-cols-2 gap-6">
            <div className="rounded-lg border border-[#161A20]/12 bg-white p-6">
              <h3 className="font-bold text-lg mb-3 flex items-center gap-2"><Trash2 className="w-4 h-4 text-red-600" /> What is deleted</h3>
              <ul className="space-y-2 text-sm text-[#161A20]/75 list-disc pl-5">
                <li>Your personal profile — name, email, phone number and profile photo</li>
                <li>Your login credentials and saved sign-in / biometric settings</li>
                <li>Device push-notification tokens</li>
                <li>Your personal app preferences</li>
              </ul>
            </div>
            <div className="rounded-lg border border-[#161A20]/12 bg-white p-6">
              <h3 className="font-bold text-lg mb-3 flex items-center gap-2"><ShieldCheck className="w-4 h-4" style={{ color: PRIMARY }} /> What may be kept</h3>
              <ul className="space-y-2 text-sm text-[#161A20]/75 list-disc pl-5">
                <li>Fieldlink is a business tool used by your employer. Work records you created or that relate to you — timesheets, job records, health &amp; safety records and invoices — are owned by your organisation.</li>
                <li>Your organisation and Fieldlink may retain these to meet legal, tax, payroll and health-&amp;-safety obligations (New Zealand law generally requires employment and tax records to be kept for <b>7 years</b>).</li>
                <li>Where retained, such records are deleted or anonymised once the retention period ends.</li>
              </ul>
            </div>
          </section>

          {/* Retention */}
          <section className="rounded-lg border border-[#161A20]/12 bg-white p-6 flex flex-wrap gap-6">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 mt-0.5" style={{ color: PRIMARY }} />
              <div className="text-sm text-[#161A20]/75">
                <div className="font-bold text-[#161A20]">Retention period</div>
                Personal data is removed within 30 days of a verified request. Compliance records are kept up to 7 years as required by NZ law, then deleted or anonymised. Backups are purged within 90 days.
              </div>
            </div>
          </section>

          {/* Form */}
          <section className="rounded-lg border border-[#161A20]/12 bg-white p-6 md:p-8">
            <h2 className="font-display font-black uppercase tracking-tight text-2xl mb-1 flex items-center gap-2">
              <Mail className="w-5 h-5" style={{ color: PRIMARY }} /> Submit a request
            </h2>
            <p className="text-sm text-[#161A20]/60 mb-6">We’ll email you to confirm before anything is deleted.</p>

            {done ? (
              <div className="flex items-start gap-3 rounded-md bg-green-50 border border-green-200 p-5" data-testid="delete-account-success">
                <CheckCircle2 className="w-6 h-6 text-green-600 shrink-0" />
                <div className="text-sm text-green-900">
                  <div className="font-bold mb-1">Request received</div>
                  We’ve logged your request and our team will email you to confirm, then action it within 30 days.
                </div>
              </div>
            ) : (
              <form onSubmit={submit} noValidate className="space-y-4" data-testid="delete-account-form">
                <div className="grid md:grid-cols-2 gap-4">
                  <label className="block">
                    <span className="text-xs font-bold uppercase tracking-wider text-[#161A20]/60">Full name *</span>
                    <input value={form.name} onChange={set("name")} required data-testid="delete-name-input"
                      className="mt-1 w-full h-11 rounded-sm border border-[#161A20]/25 bg-white px-3 outline-none focus:border-[#FF5F15]" />
                  </label>
                  <label className="block">
                    <span className="text-xs font-bold uppercase tracking-wider text-[#161A20]/60">Account email *</span>
                    <input type="email" value={form.email} onChange={set("email")} required data-testid="delete-email-input"
                      className="mt-1 w-full h-11 rounded-sm border border-[#161A20]/25 bg-white px-3 outline-none focus:border-[#FF5F15]" />
                  </label>
                </div>
                <label className="block">
                  <span className="text-xs font-bold uppercase tracking-wider text-[#161A20]/60">Company / organisation</span>
                  <input value={form.organisation} onChange={set("organisation")} data-testid="delete-org-input"
                    className="mt-1 w-full h-11 rounded-sm border border-[#161A20]/25 bg-white px-3 outline-none focus:border-[#FF5F15]" />
                </label>
                <label className="block">
                  <span className="text-xs font-bold uppercase tracking-wider text-[#161A20]/60">Reason (optional)</span>
                  <textarea value={form.reason} onChange={set("reason")} rows={3} data-testid="delete-reason-input"
                    className="mt-1 w-full rounded-sm border border-[#161A20]/25 bg-white px-3 py-2 outline-none focus:border-[#FF5F15]" />
                </label>
                <button type="submit" disabled={busy} data-testid="delete-submit-button"
                  className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-sm font-bold uppercase tracking-wider text-white disabled:opacity-60"
                  style={{ backgroundColor: PRIMARY }}>
                  {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Request account deletion</>}
                </button>
              </form>
            )}
          </section>

          <p className="text-sm text-[#161A20]/55">
            Questions about your data? See our <Link to="/privacy" className="font-bold" style={{ color: PRIMARY }}>Privacy Policy</Link> or
            email <a href="mailto:support@fieldlink.co.nz" className="font-bold" style={{ color: PRIMARY }}>support@fieldlink.co.nz</a>.
          </p>
        </main>

        <div><MarketingFooter /></div>
      </div>
    </>
  );
}
